import type {
  InboxWorkflowJson,
  InboxWorkflowFlowDefinition,
  InboxWorkflowStepDefinition,
  InboxWorkflowStepConfig,
  InboxWorkflowTableColumn,
  InboxWorkflowTableRuntimeRow,
  InboxWorkflowTableStepConfig,
} from "@kontextwerk/control-web-frame/inbox-workflow/contract"
import {
  composeInboxWorkflowFlowSteps,
  inboxWorkflowArrayObjects,
  inboxWorkflowColumn,
  inboxWorkflowObjectValue,
  inboxWorkflowPartialMessage,
  inboxWorkflowRuntimeRows,
  inboxWorkflowStringValue,
} from "@kontextwerk/control-web-frame/inbox-workflow/contract"
import type { InboxWorkflowHelpDialogId } from "../help-dialog-ids"
import type { InboxCaseWorkflowRowOutcomeMap } from "../result-outcomes"

type EngineColumn = InboxWorkflowTableColumn

const BATCH_MODE_BATCH = "batch"

export const objectValue = inboxWorkflowObjectValue
export const arrayObjects = inboxWorkflowArrayObjects
export const stringValue = inboxWorkflowStringValue

export const column = (
  id: string,
  label: string,
  cellKey: string,
  extra: Omit<Partial<EngineColumn>, "id" | "label" | "cellKey"> = {},
): EngineColumn => inboxWorkflowColumn(id, label, cellKey, extra)

export const runtimeRows = <Row extends { actionCaseId: string }>(
  rows: Row[],
): InboxWorkflowTableRuntimeRow[] => inboxWorkflowRuntimeRows(rows)

export const partialMessage = inboxWorkflowPartialMessage

export const legacyRowOutcomesFor = (
  rowOutcomes: Record<string, { status: string; detail?: string | null; error?: string | null }>,
  stepKey: string,
): InboxCaseWorkflowRowOutcomeMap =>
  Object.fromEntries(
    Object.entries(rowOutcomes).map(([actionCaseId, outcome]) => {
      const status =
        outcome.status === "failed" || outcome.status === "skipped" ? outcome.status : "success"
      const message = stringValue(outcome.error, outcome.detail)

      return [
        actionCaseId,
        {
          actionCaseId,
          stepKey,
          status,
          label:
            status === "failed" ? "Prüfung nötig" : status === "skipped" ? "Warnung" : "Erledigt",
          message,
          hint:
            status === "success"
              ? "Die erfolgreiche Änderung wurde gespeichert."
              : "Die Zeile bleibt prüfbar.",
        },
      ]
    }),
  ) as InboxCaseWorkflowRowOutcomeMap

export const buildFlowSteps = (
  entries: Record<string, InboxWorkflowTableStepConfig | InboxWorkflowStepConfig>,
) => composeInboxWorkflowFlowSteps(entries)

export const withHelp = <Step extends InboxWorkflowStepConfig>(
  step: Step,
  textId: InboxWorkflowHelpDialogId,
): Step => ({
  ...step,
  help: {
    ...(step.help ?? {}),
    textId,
  },
})

export const workflowStep = ({
  stepKey,
  label,
  componentKey,
  requirements,
  outputKey = null,
}: {
  stepKey: string
  label: string
  componentKey: string
  requirements: string[]
  outputKey?: string | null
}): InboxWorkflowStepDefinition => ({
  step_key: stepKey,
  label,
  component_key: componentKey,
  requirements_resolved_by_step: requirements,
  batch_mode: BATCH_MODE_BATCH,
  output_key: outputKey,
})

export const workflowDefinition = ({
  flowKey,
  label,
  subjectPolicy,
  requirements,
  steps,
}: {
  flowKey: string
  label: string
  subjectPolicy: "REQUIRED" | "OPTIONAL" | "FORBIDDEN"
  requirements: string[]
  steps: InboxWorkflowStepDefinition[]
}): InboxWorkflowFlowDefinition => ({
  flow_key: flowKey,
  label,
  batch_mode: BATCH_MODE_BATCH,
  subject_policy: subjectPolicy,
  allowed_subject_case_types: subjectPolicy === "FORBIDDEN" ? [] : ["kehrblatt.work_order"],
  requirements,
  steps,
})
